import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { XIcon } from 'lucide-react';
type ModalType = 'privacy' | 'terms' | null;
const privacyContent = {
  title: 'Política de Privacidad',
  sections: [{
    heading: 'Información que Recopilamos',
    text: 'Recopilamos únicamente la información necesaria para procesar tu compra y entregarte el producto digital. Esto incluye tu nombre, dirección de correo electrónico y datos de pago procesados de forma segura a través de nuestro proveedor de pagos.'
  }, {
    heading: 'Uso de la Información',
    text: 'Tu información personal se utiliza exclusivamente para: procesar tu pedido, enviarte el producto adquirido, y comunicarte actualizaciones importantes relacionadas con tu compra. Nunca vendemos, alquilamos ni compartimos tu información personal con terceros con fines de marketing.'
  }, {
    heading: 'Protección de Datos',
    text: 'Implementamos medidas de seguridad estándar de la industria para proteger tu información personal. Todos los pagos se procesan a través de plataformas seguras con encriptación SSL.'
  }, {
    heading: 'Cookies',
    text: 'Nuestro sitio puede utilizar cookies esenciales para garantizar el funcionamiento correcto de la plataforma. No utilizamos cookies de seguimiento publicitario.'
  }, {
    heading: 'Tus Derechos',
    text: 'Tienes derecho a acceder, rectificar o eliminar tus datos personales en cualquier momento. Para ejercer estos derechos, puedes comunicarte con nosotros a través de los canales indicados en tu correo de confirmación de compra.'
  }]
};
const termsContent = {
  title: 'Términos y Condiciones',
  sections: [{
    heading: 'Producto Digital',
    text: 'El Kit de Emergencia: Adiós al Impuesto TDAH es un producto digital educativo. Al completar tu compra, recibirás acceso inmediato al contenido descargable. Este producto tiene fines informativos y educativos únicamente.'
  }, {
    heading: 'Uso Permitido',
    text: 'El producto es para uso personal e individual. No está permitido redistribuir, revender, compartir públicamente ni reproducir el contenido del kit sin autorización expresa por escrito.'
  }, {
    heading: 'Descargo de Responsabilidad',
    text: 'Este producto no constituye asesoramiento financiero, médico ni psicológico profesional. No sustituye el diagnóstico ni tratamiento del TDAH por parte de profesionales de la salud. Los resultados pueden variar según cada persona.'
  }, {
    heading: 'Política de Reembolso',
    text: 'Debido a la naturaleza digital del producto y al acceso inmediato tras la compra, todas las ventas son finales. Si experimentas problemas técnicos para acceder al contenido, te ayudaremos a resolverlos.'
  }, {
    heading: 'Modificaciones',
    text: 'Nos reservamos el derecho de actualizar estos términos en cualquier momento. Las modificaciones entrarán en vigor a partir de su publicación en esta página. El uso continuado del producto implica la aceptación de los términos actualizados.'
  }, {
    heading: 'Propiedad Intelectual',
    text: 'Todo el contenido del Kit de Emergencia, incluyendo textos, plantillas, guiones y herramientas, está protegido por derechos de autor. Quiet Premium es la titular de todos los derechos de propiedad intelectual asociados.'
  }]
};
export function Footer() {
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  return <>
      <footer className="bg-brand-teal text-brand-cream/60 py-12 text-center" data-id="element-42">
        <div className="max-w-7xl mx-auto px-6 lg:px-8" data-id="element-43">
          <p className="font-serif text-lg mb-4 text-brand-gold" data-id="element-44">
            Quiet Premium
          </p>
          <p className="text-sm" data-id="element-45">
            &copy; {new Date().getFullYear()} El Kit de Emergencia. Todos los
            derechos reservados.
          </p>
          <p className="text-xs mt-4 max-w-2xl mx-auto opacity-50" data-id="element-46">
            Este producto no sustituye el consejo financiero profesional ni el
            tratamiento médico o psicológico para el TDAH.
          </p>

          <div className="flex items-center justify-center gap-6 mt-6" data-id="element-47">
            <button onClick={() => setActiveModal('privacy')} className="text-sm text-brand-cream/50 hover:text-brand-gold transition-colors duration-200 underline underline-offset-4 decoration-brand-cream/20 hover:decoration-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-teal rounded" data-id="element-48">
              Política de Privacidad
            </button>
            <span className="text-brand-cream/20" data-id="element-49">·</span>
            <button onClick={() => setActiveModal('terms')} className="text-sm text-brand-cream/50 hover:text-brand-gold transition-colors duration-200 underline underline-offset-4 decoration-brand-cream/20 hover:decoration-brand-gold focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold focus-visible:ring-offset-2 focus-visible:ring-offset-brand-teal rounded" data-id="element-50">
              Términos y Condiciones
            </button>
          </div>
        </div>
      </footer>

      <AnimatePresence data-id="element-51">
        {activeModal && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} className="fixed inset-0 z-50 flex items-center justify-center p-4 md:p-8" onClick={() => setActiveModal(null)} data-id="element-52">
            {/* Backdrop */}
            <div className="absolute inset-0 bg-brand-teal/80 backdrop-blur-sm" data-id="element-53" />

            {/* Modal */}
            <motion.div initial={{
          opacity: 0,
          y: 30,
          scale: 0.97
        }} animate={{
          opacity: 1,
          y: 0,
          scale: 1
        }} exit={{
          opacity: 0,
          y: 20,
          scale: 0.97
        }} transition={{
          duration: 0.3,
          ease: [0.21, 0.47, 0.32, 0.98]
        }} onClick={e => e.stopPropagation()} className="relative bg-brand-cream rounded-2xl shadow-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden" data-id="element-54">
              {/* Header */}
              <div className="sticky top-0 bg-brand-cream border-b border-brand-teal/10 px-6 md:px-8 py-5 flex items-center justify-between z-10" data-id="element-55">
                <h3 className="text-xl md:text-2xl font-serif font-bold text-brand-teal" data-id="element-56">
                  {activeModal === 'privacy' ? privacyContent.title : termsContent.title}
                </h3>
                <button onClick={() => setActiveModal(null)} className="w-10 h-10 rounded-full flex items-center justify-center text-brand-gray hover:text-brand-teal hover:bg-brand-teal/5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-gold" aria-label="Cerrar" data-id="element-57">
                  <XIcon className="w-5 h-5" data-id="element-58" />
                </button>
              </div>

              {/* Content */}
              <div className="px-6 md:px-8 py-6 overflow-y-auto max-h-[calc(85vh-72px)]" data-id="element-59">
                <div className="space-y-8" data-id="element-60">
                  {(activeModal === 'privacy' ? privacyContent.sections : termsContent.sections).map((section, index) => <div key={index} data-id="element-61">
                      <h4 className="font-serif font-bold text-brand-teal text-lg mb-2" data-id="element-62">
                        {section.heading}
                      </h4>
                      <p className="text-brand-charcoal/75 leading-relaxed" data-id="element-63">
                        {section.text}
                      </p>
                    </div>)}
                </div>

                <div className="mt-10 pt-6 border-t border-brand-teal/10" data-id="element-64">
                  <p className="text-xs text-brand-gray/60 text-center" data-id="element-65">
                    Última actualización: Marzo 2026
                  </p>
                </div>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </>;
}